import { clone, cloneDeep } from "lodash";
import { mat2d, vec2 } from "gl-matrix";

import checkIntersection, { INTERSECTING } from "@/utils/math/Intersection.mjs";

export const CENTER = "center";
const TOP_LEFT = "tl";
const TOP_RIGHT = "tr";
const BOTTOM_RIGHT = "br";
const BOTTOM_LEFT = "bl";

export const LEFT = "left";
export const TOP = "top";
export const RIGHT = "right";
export const BOTTOM = "bottom";

/**
 * @typedef {import("react").RefObject} RefObject
 * @typedef {import("@/types/MathTypes.mjs").Coordinates} Coordinates
 * @typedef {import("@/types/MathTypes.mjs").Ray} Ray
 *
 * @typedef {mat2d | [number, number, number, number, number, number]} matrix
 *
 * @typedef {{
 *  [BOTTOM]: number,
 *  height: number
 *  [LEFT]: number,
 *  [RIGHT]: number,
 *  [TOP]: number,
 *  width: number
 * }} Bounds
 *
 * @typedef {{
 *  [BOTTOM_LEFT]: Coordinates
 *  [BOTTOM_RIGHT]: Coordinates,
 *  [CENTER]: Coordinates,
 *  [TOP_LEFT]: Coordinates,
 *  [TOP_RIGHT]: Coordinates,
 * }} MatrixBoundingCorners
 *
 * @typedef {{
 * [BOTTOM]: Ray,
 * [LEFT]: Ray,
 * [RIGHT]: Ray,
 * [TOP]: Ray
 * }} BoundingEdges
 *
 * @typedef {{
 *  point: Coordinates,
 *  comparisonEdge: Ray,
 *  baseEdge: Ray
 *  baseEdgeName: keyof typeof EDGES | string
 * }} SingleEdgeIntersectionResult
 */

// * DO NOT REORDER THESE KEYS

const CORNERS = {
  [BOTTOM_LEFT]: BOTTOM_LEFT,
  [BOTTOM_RIGHT]: BOTTOM_RIGHT,
  [TOP_LEFT]: TOP_LEFT,
  [TOP_RIGHT]: TOP_RIGHT,
};

const EDGES = {
  [BOTTOM]: BOTTOM,
  [LEFT]: LEFT,
  [RIGHT]: RIGHT,
  [TOP]: TOP,
};

/** @type {Object.<keyof EDGES, Array<[CORNERS[keyof CORNERS], CORNERS[keyof CORNERS]]>>} */
const EDGE_CORNER_SETS = {
  [TOP]: [TOP_LEFT, TOP_RIGHT],
  [RIGHT]: [TOP_RIGHT, BOTTOM_RIGHT],
  [BOTTOM]: [BOTTOM_RIGHT, BOTTOM_LEFT],
  [LEFT]: [BOTTOM_LEFT, TOP_LEFT],
};

/** @type {MatrixBoundingCorners} */
const BASE_MATRIX_BOUNDS = {
  [BOTTOM_LEFT]: [0, 0],
  [BOTTOM_RIGHT]: [0, 0],
  [CENTER]: [0, 0],
  [TOP_LEFT]: [0, 0],
  [TOP_RIGHT]: [0, 0],
};

const BASE_BOUNDING_EDGES = {
  [BOTTOM]: null,
  [LEFT]: null,
  [RIGHT]: null,
  [TOP]: null,
};

/**
 * Retrieves the scroll positions of an element.
 * @param {HTMLElement} element - The element to get the scroll positions for.
 * @returns {Coordinates} An array containing the scrollLeft and scrollTop values.
 */
export const getScrollPositions = (element) => [element.scrollLeft, element.scrollTop];

/**
 * Constrains an element's position within the bounds of a container.
 * @param {Object} params - The parameters for constraining the element.
 * @param {Object} params.elementBounds - The bounds of the element.
 * @param {Object} params.containerBounds - The bounds of the container.
 * @returns {Coordinates} The constrained position as [left, top].
 */
export const constrainToBounds = ({ elementBounds, containerBounds }) => {
  /** @type {Coordinates} */
  const position = [elementBounds.left, elementBounds.top];

  const withinLeft = elementBounds.left >= containerBounds.left;
  const withinTop = elementBounds.top >= containerBounds.top;
  const withinRight = elementBounds.right <= containerBounds.right;
  const withinBottom = elementBounds.bottom <= containerBounds.bottom;

  if (!withinLeft) position[0] = containerBounds.left;
  if (!withinTop) position[1] = containerBounds.top;
  if (!withinRight) position[0] = containerBounds.right - elementBounds.width;
  if (!withinBottom) position[1] = containerBounds.bottom - elementBounds.height;

  return position;
};

/**
 * Calculates the visible bounds of the stage, relative to the stage without left/right offsets.
 * @param {Bounds} stageBounds - The bounds of the stage.
 * @param {Coordinates} scrollPositions - The current scroll positions.
 * @returns {Bounds} The visible bounds of the stage.
 */
export const getVisibleStageBounds = (stageBounds, scrollPositions) => {
  return {
    left: scrollPositions[0],
    top: scrollPositions[1],
    right: scrollPositions[0] + stageBounds.width,
    bottom: scrollPositions[1] + stageBounds.height,
    width: stageBounds.width,
    height: stageBounds.height,
  };
};

/**
 * Applies additional values to the target bounds.
 * @param {Bounds} target - The target bounds to apply values to.
 * @param {Partial<Bounds>} values - The values to apply.
 * @returns {Bounds} The resulting bounds after applying the values.
 */
export const applyBounds = (target, values) =>
  Object.entries(target).reduce((result, [key, value]) => {
    result[key] = values[key] !== undefined ? value + values[key] : value;
    return result;
  }, cloneDeep(target));

/**
 * Returns bounding points (corners and center) from a set of bounds
 * @param {Bounds} bounds
 * @return {MatrixBoundingCorners}
 */
export const getElementBoundingPoints = (bounds) => ({
  [CENTER]: [bounds.left + bounds.width / 2, bounds.top + bounds.height / 2],
  [TOP_LEFT]: [bounds.left, bounds.top],
  [TOP_RIGHT]: [bounds.left + bounds.width, bounds.top],
  [BOTTOM_RIGHT]: [bounds.left + bounds.width, bounds.top + bounds.height],
  [BOTTOM_LEFT]: [bounds.left, bounds.top + bounds.height],
});

/**
 * Applies a matrix to a vector, effectively shifting it in relation to the matrix space.
 * @param {matrix} matrix
 * @param {Coordinates} point
 * @returns
 */
export const applyMatrix = (matrix, point) => vec2.transformMat2d(vec2.create(), point, matrix);

/**
 * Shifts a set of MatrixBoundingCorners to a matrix's space by applying the matrix to each value.
 * @param {matrix} matrix
 * @param {MatrixBoundingCorners} valueObject
 * @returns {MatrixBoundingCorners}
 */
export const applyMatrixToObject = (matrix, valueObject) =>
  Object.entries(valueObject).reduce((result, [key, value]) => {
    result[key] = applyMatrix(matrix, value);
    return result;
  }, cloneDeep(BASE_MATRIX_BOUNDS));

/**
 * Returns a ray based in global space. Vertical from the center.
 * @param {MatrixBoundingCorners} selectionMatrixBounds
 * @param {number} length - length of the ray to generate. Should be arbitrarily long within reason.
 * @param {TOP=} direction
 * @returns {Ray}
 */
export const getVerticalRayFromCenter = (selectionMatrixBounds, length, direction = "top") => {
  /** @type {Coordinates} */
  const globalSelectionRayEnd = [
    selectionMatrixBounds.center[0],
    selectionMatrixBounds.center[1] - length,
  ];
  return [selectionMatrixBounds.center, globalSelectionRayEnd];
};

/**
 * Creates a set of boundingEdges from MatrixBoundingCorners.
 * @param {MatrixBoundingCorners} bounds
 * @returns {BoundingEdges}
 */
export const collectEdgesFromCorners = (bounds) =>
  Object.entries(EDGE_CORNER_SETS).reduce((result, [edge, [cornerKey1, cornerKey2]]) => {
    const cornerPoint1 = bounds[cornerKey1];
    const cornerPoint2 = bounds[cornerKey2];
    result[edge] = [cornerPoint1, cornerPoint2];
    return result;
  }, cloneDeep(BASE_BOUNDING_EDGES));

/**
 * Gets the first intersection from the baseEdge intersecting each of the edges, if one happens.
 * @param {Array<Ray>} edges - Edges in a list
 * @param {Ray} baseEdge - The line potentially intersecting the edges
 * @returns {SingleEdgeIntersectionResult=}
 */
export const getFirstEdgeIntersection = (edges, baseEdge, baseEdgeName = CENTER) => {
  // todo: may need to be zoomed

  for (const comparisonEdge of edges) {
    const intersectionPoint = checkIntersection(comparisonEdge, baseEdge);
    if (intersectionPoint.type === INTERSECTING)
      return {
        point: intersectionPoint.point,
        comparisonEdge,
        baseEdge,
        baseEdgeName,
      };
  }
};

/**
 * Collects a list of intersections between two sets of edges. Answers the question
 * "what edges from the comparison set intersect the base set and where?"
 * @param {BoundingEdges} baseEdges - base set of edges
 * @param {BoundingEdges} comparisonEdges - set of edges to compare, potentially intersecting the base
 * @returns {Array<SingleEdgeIntersectionResult>}
 */
export const collectEdgeIntersections = (baseEdges, comparisonEdges) => {
  const comparisonEdgeValues = Object.values(comparisonEdges);

  return Object.entries(baseEdges).reduce((intersectionCollection, [edgeName, baseEdge]) => {
    const intersection = getFirstEdgeIntersection(comparisonEdgeValues, baseEdge, edgeName);
    intersection && intersectionCollection.push(intersection);
    return intersectionCollection;
  }, []);
};
